import { API_BASE_URL } from '@volee/config'

import { TransportError } from './errors.ts'
import { toApiError, toTransportError } from './parse.ts'
import type { JsonValue, QueryParams, RequestOptions, TokenProvider } from './types.ts'

// The typed fetch: the one place that knows the base URL, the Bearer header, the 401 dance
// and the RFC 9457 error shape. The reasoning for each is in this package's README.

export interface ApiClientOptions {
  /** Defaults to `API_BASE_URL` from `@volee/config`. */
  readonly baseUrl?: string
  /** Supplies and refreshes the access token. Injected and not imported, so this package does
   *  not depend on `@volee/auth`: README, «Perché il token si inietta». */
  readonly tokens?: TokenProvider
  /** Injected in tests. Defaults to the global `fetch`. */
  readonly fetch?: typeof fetch
}

export interface ApiClient {
  request<TResponse>(path: string, options?: RequestOptions): Promise<TResponse>
  get<TResponse>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<TResponse>
  post<TResponse>(
    path: string,
    body?: JsonValue | FormData,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<TResponse>
  patch<TResponse>(
    path: string,
    body?: JsonValue | FormData,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<TResponse>
  put<TResponse>(
    path: string,
    body?: JsonValue | FormData,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<TResponse>
  delete<TResponse>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<TResponse>
}

function encodeBody(body: RequestOptions['body']): BodyInit | undefined {
  if (body === undefined) return undefined
  return body instanceof FormData ? body : JSON.stringify(body)
}

function buildUrl(baseUrl: string, path: string, query?: QueryParams): string {
  const url = new URL(`${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      // Absent and present-but-empty are different things; only the former is dropped.
      if (value != null) url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

/** The credential, or `null` with no session. A rejection is the third state, and it used to
 *  escape before any 401 handling — so nothing announced the commonest way a session ends. */
async function credential(tokens: TokenProvider): Promise<string | null> {
  try {
    return await tokens.getToken()
  } catch (cause) {
    tokens.onSessionExpired?.()
    throw cause
  }
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? API_BASE_URL
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)
  const tokens = options.tokens

  // The in-flight refresh, which is the deduplication: ten 401s await one promise. Why that
  // is correctness and not an optimisation: README, «Refresh al 401».
  let inFlightRefresh: Promise<string | null> | null = null

  function refreshOnce(): Promise<string | null> {
    if (!tokens) return Promise.resolve(null)
    inFlightRefresh ??= tokens
      .refresh()
      // A failed refresh is a dead session, not an exception the caller asked for: the
      // original 401 is what gets reported, so this resolves to null instead of throwing.
      .catch(() => null)
      .finally(() => {
        inFlightRefresh = null
      })
    return inFlightRefresh
  }

  async function send(
    url: string,
    options: RequestOptions,
    token: string | null,
  ): Promise<Response> {
    const headers = new Headers(options.headers)
    // A FormData sets its own Content-Type: only fetch knows the multipart boundary,
    // so writing the header here would produce a body the server cannot parse.
    const isForm = options.body instanceof FormData
    if (options.body !== undefined && !isForm) headers.set('Content-Type', 'application/json')
    if (token) headers.set('Authorization', `Bearer ${token}`)
    // Both problem+json and plain json come back from this API.
    if (!headers.has('Accept')) headers.set('Accept', 'application/json, application/problem+json')

    try {
      return await doFetch(url, {
        method: options.method ?? 'GET',
        headers,
        body: encodeBody(options.body),
        signal: options.signal,
      })
    } catch (cause) {
      throw toTransportError(cause)
    }
  }

  async function parseBody<TResponse>(response: Response): Promise<TResponse> {
    // 204, and any other answer with no content, are legitimate successes.
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as TResponse
    }
    const text = await response.text()
    if (!text) return undefined as TResponse
    try {
      return JSON.parse(text) as TResponse
    } catch (cause) {
      throw new TransportError(
        'malformed_response',
        `Expected JSON from ${response.url} but the body could not be parsed`,
        { cause },
      )
    }
  }

  async function request<TResponse>(
    path: string,
    requestOptions: RequestOptions = {},
  ): Promise<TResponse> {
    const url = buildUrl(baseUrl, path, requestOptions.query)
    const wantsAuth = requestOptions.authenticated !== false && tokens !== undefined

    let token = wantsAuth ? await credential(tokens) : null
    let response = await send(url, requestOptions, token)

    // One retry, and only for a 401 on an authenticated request. A second 401 after a
    // fresh token means the session is genuinely gone, not that the token was stale.
    if (response.status === 401 && wantsAuth) {
      const refreshed = await refreshOnce()
      if (refreshed) {
        token = refreshed
        response = await send(url, requestOptions, token)
      }
      if (response.status === 401) {
        // Only for a session that existed — `token` is non-null exactly then. A 401 with no
        // credential means "nobody is signed in", which is an answer, not an expiry.
        if (token !== null) tokens.onSessionExpired?.()
        throw await toApiError(response)
      }
    }

    if (!response.ok) throw await toApiError(response)
    return parseBody<TResponse>(response)
  }

  return {
    request,
    get: (path, options) => request(path, { ...options, method: 'GET' }),
    post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
    patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
    put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
    delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  }
}
